import type ExcelJS from 'exceljs';
import {
    UNIT_OPTIONS,
    VAT_OPTIONS,
} from '@/pages/purchases/manualImport/manualImportMeta';
import {
    REFERENCE_SHEETS,
    TEMPLATE_BODY_ROWS,
    TEMPLATE_COLUMNS,
    type PurchaseTemplateData,
    type PurchaseTemplateLabels,
    type ReferenceSheetSpec,
    type TemplateDropdownSource,
} from './purchaseTemplateMeta';

/**
 * Builds the purchase-import Excel template at click time so the Supplier /
 * Category / Brand dropdowns reflect the live catalog. The hook serializes and
 * downloads it through the shared `@/utils/fileDownload`. exceljs
 * is ~1.4 MB, so it's loaded via dynamic `import()` here — kept out of the main
 * bundle and only fetched when a user actually downloads a template.
 *
 * The builder is pure of React/i18next: the caller passes already-resolved
 * `labels`, so it unit-tests directly. See `purchaseTemplateMeta.ts` for the
 * column + reference-sheet spec it renders.
 */

/**
 * The two supported i18n languages — kept narrow so an unknown language value
 * can't slip through and silently pick the wrong filename.
 */
export type ExcelTemplateLanguage = 'en' | 'ka';

/** Human-friendly download filename per locale (the Georgian one stays Georgian). */
const TEMPLATE_FILENAME: Record<ExcelTemplateLanguage, string> = {
    en: 'BOS_purchase_template.xlsx',
    ka: 'BOS_შესყიდვის_შაბლონი.xlsx',
};

/**
 * Map an i18next language code (with possible region tag, e.g. `en-US`) to one
 * of the two supported languages. Defaults to English so an unconfigured /
 * unknown language still produces a usable download.
 */
export const resolveTemplateLanguage = (
    rawLanguage: string,
): ExcelTemplateLanguage => {
    const normalised = rawLanguage.slice(0, 2).toLowerCase();
    return normalised === 'ka' ? 'ka' : 'en';
};

/** Download filename for the active language. */
export const getTemplateFilename = (rawLanguage: string): string =>
    TEMPLATE_FILENAME[resolveTemplateLanguage(rawLanguage)];

/** Quote a sheet name for use inside a defined-name / range reference. */
const sheetRef = (name: string): string => `'${name.replace(/'/g, "''")}'`;

/** The data-validation `formula1` for a dropdown column's source. */
const dropdownFormula = (
    source: TemplateDropdownSource,
    refByKey: Map<string, ReferenceSheetSpec>,
): string => {
    if (source === 'units') {
        return `"${UNIT_OPTIONS.join(',')}"`;
    }
    if (source === 'vat') {
        return `"${VAT_OPTIONS.join(',')}"`;
    }
    // DB-backed: point at the reference sheet's defined name.
    return refByKey.get(source)?.definedName ?? '';
};

/**
 * Assemble the workbook: a main "Products" sheet (headers + per-column
 * dropdowns) plus one reference sheet per DB-backed dropdown, wired together by
 * workbook-scoped defined names.
 */
export const buildPurchaseTemplateWorkbook = async (
    data: PurchaseTemplateData,
    labels: PurchaseTemplateLabels,
): Promise<ExcelJS.Workbook> => {
    const { Workbook } = (await import('exceljs')).default;
    const workbook = new Workbook();

    // Products first — it's the sheet users land on when opening the file. The
    // validation formulae reference the defined names as plain strings, so the
    // reference sheets can be created afterwards.
    const products = workbook.addWorksheet(labels.sheets.products);
    products.views = [{ state: 'frozen', ySplit: 1 }];

    const refByKey = new Map<string, ReferenceSheetSpec>();
    for (const ref of REFERENCE_SHEETS) {
        refByKey.set(ref.key, ref);
        const sheetName = labels.sheets[ref.key];
        const sheet = workbook.addWorksheet(sheetName);
        sheet.getColumn(1).width = 32;
        sheet.getCell('A1').value = sheetName;
        sheet.getCell('A1').font = { bold: true };

        const values = data[ref.key];
        values.forEach((name, index) => {
            sheet.getCell(`A${index + 2}`).value = name;
        });

        // Defined name over the value range (≥ A2 even when empty, so the name
        // always resolves to a valid single-cell range).
        const lastRow = Math.max(values.length + 1, 2);
        workbook.definedNames.add(
            `${sheetRef(sheetName)}!$A$2:$A$${lastRow}`,
            ref.definedName,
        );
    }

    const headerRow = products.addRow(
        TEMPLATE_COLUMNS.map(
            (column) => labels.columns[column.key] ?? column.key,
        ),
    );
    headerRow.font = { bold: true };

    const lastBodyRow = TEMPLATE_BODY_ROWS + 1; // row 1 is the header

    TEMPLATE_COLUMNS.forEach((column, index) => {
        const columnNumber = index + 1;
        const sheetColumn = products.getColumn(columnNumber);
        sheetColumn.width = column.width;
        if (column.kind === 'date') {
            sheetColumn.numFmt = 'yyyy-mm-dd';
        }

        if (column.kind !== 'dropdown' || !column.source) {
            return;
        }

        const formula = dropdownFormula(column.source, refByKey);
        if (!formula) {
            return;
        }

        // exceljs only exposes validation per cell, so apply the list
        // validation to every body cell in the column.
        for (let row = 2; row <= lastBodyRow; row += 1) {
            products.getCell(row, columnNumber).dataValidation = {
                type: 'list',
                allowBlank: !column.required,
                formulae: [formula],
            };
        }
    });

    return workbook;
};
